"use strict";
/**
 * @license
 * Copyright 2019 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { binarySearchLowerBound } from "#src/util/array.js";
export const preferredSiPrefixes = [
  { prefix: "Y", exponent: 24, longPrefix: "yotta" },
  { prefix: "Z", exponent: 21, longPrefix: "zetta" },
  { prefix: "E", exponent: 18, longPrefix: "exa" },
  { prefix: "P", exponent: 15, longPrefix: "peta" },
  { prefix: "T", exponent: 12, longPrefix: "tera" },
  { prefix: "G", exponent: 9, longPrefix: "giga" },
  { prefix: "M", exponent: 6, longPrefix: "mega" },
  { prefix: "k", exponent: 3, longPrefix: "kilo" },
  { prefix: "", exponent: 0, longPrefix: "" },
  { prefix: "m", exponent: -3, longPrefix: "milli" },
  { prefix: "\xB5", exponent: -6, longPrefix: "micro" },
  { prefix: "n", exponent: -9, longPrefix: "nano" },
  { prefix: "p", exponent: -12, longPrefix: "pico" },
  { prefix: "f", exponent: -15, longPrefix: "femto" },
  { prefix: "a", exponent: -18, longPrefix: "atto" },
  { prefix: "z", exponent: -21, longPrefix: "zepto" },
  { prefix: "y", exponent: -24, longPrefix: "yocto" }
];
export const allSiPrefixes = [
  ...preferredSiPrefixes,
  { prefix: "h", exponent: 2, longPrefix: "hecto" },
  { prefix: "da", exponent: 1, longPrefix: "deca" },
  { prefix: "d", exponent: -1, longPrefix: "deci" },
  { prefix: "c", exponent: -2, longPrefix: "centi" }
];
const siPrefixesWithAlternatives = [
  { prefix: "u", exponent: -6 },
  // Also allow "u" for micro
  ...allSiPrefixes
];
export const supportedUnits = /* @__PURE__ */ new Map();
supportedUnits.set("", { unit: "", exponent: 0 });
export const exponentToPrefix = /* @__PURE__ */ new Map();
for (const { prefix, exponent } of siPrefixesWithAlternatives) {
  exponentToPrefix.set(exponent, prefix);
  for (const unit of ["m", "s", "Hz", "rad/s"]) {
    supportedUnits.set(`${prefix}${unit}`, { unit, exponent });
  }
}
export function pickSiPrefix(x) {
  const exponent = Math.log10(x);
  const numPrefixes = preferredSiPrefixes.length;
  const i = binarySearchLowerBound(
    0,
    numPrefixes,
    (i2) => preferredSiPrefixes[i2].exponent <= exponent
  );
  return preferredSiPrefixes[Math.min(i, numPrefixes - 1)];
}
export function formatScaleWithUnit(scale, unit, options = {}) {
  const { precision = 6, elide1 = true } = options;
  let adjustedScale = scale;
  let prefix = "";
  if (unit !== "") {
    const result = pickSiPrefix(scale);
    prefix = result.prefix;
    adjustedScale = scaleByExp10(scale, -result.exponent);
  }
  if (elide1 && adjustedScale === 1) {
    return { scale: "", unit, prefix };
  }
  let scaleString;
  if (precision !== 0) {
    if (adjustedScale < 1 || adjustedScale >= 1e3) {
      scaleString = adjustedScale.toPrecision(precision);
    } else {
      scaleString = adjustedScale.toFixed(precision);
    }
    const eIndex = scaleString.indexOf("e");
    let numString;
    let exponentString;
    if (eIndex !== -1) {
      numString = scaleString.substring(0, eIndex);
      exponentString = scaleString.substring(eIndex);
    } else {
      numString = scaleString;
      exponentString = "";
    }
    const m = numString.match(/.*\.(?:[0-9]*[1-9])?(0+)$/);
    if (m !== null) {
      numString = numString.substring(0, numString.length - m[1].length);
      if (numString.endsWith(".")) {
        numString = numString.substring(0, numString.length - 1);
      }
      scaleString = numString + exponentString;
    }
  } else {
    scaleString = adjustedScale.toString();
  }
  return {
    scale: scaleString,
    unit,
    prefix
  };
}
export function formatScaleWithUnitAsString(scale, unit, options) {
  const {
    scale: formattedScale,
    unit: formattedUnit,
    prefix
  } = formatScaleWithUnit(scale, unit, options);
  return `${formattedScale}${prefix}${formattedUnit}`;
}
export function parseScale(s) {
  if (s === "") {
    return { scale: 1, unit: "" };
  }
  const match = s.match(
    /^((?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+-]?\d+)?)?([µa-zA-Z]+)?$/
  );
  if (match === null) return void 0;
  const scaleString = match[1];
  let scale = scaleString === void 0 ? 1 : Number(scaleString);
  if (Number.isNaN(scale)) return void 0;
  let unit = "";
  if (match[2] !== void 0) {
    const result = supportedUnits.get(match[2]);
    if (result === void 0) {
      return void 0;
    }
    unit = result.unit;
    if (result.exponent > 0) {
      scale *= 10 ** result.exponent;
    } else {
      scale /= 10 ** -result.exponent;
    }
  }
  if (scale <= 0 || !Number.isFinite(scale)) return void 0;
  return { scale, unit };
}
export function unitFromJson(x) {
  const result = supportedUnits.get(x);
  if (result === void 0) {
    throw new Error(`Invalid unit: ${JSON.stringify(x)}`);
  }
  return result;
}
export function scaleByExp10(scale, exponent) {
  if (exponent >= 0) return scale * 10 ** exponent;
  return scale / 10 ** -exponent;
}
